import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import type { AriaAttributes, ComponentProps, ComponentType, ReactNode } from 'react'

const emptyStateVariants = cva(
  'grid justify-items-center rounded-xl border border-dashed text-center',
  {
    variants: {
      size: {
        inline: 'gap-1 px-3 py-2',
        compact: 'gap-1.5 px-4 py-5',
        default: 'gap-3 px-6 py-10',
        spacious: 'gap-4 px-8 py-16',
      },
      surface: {
        muted: 'border-border/70 bg-muted/15',
        card: 'border-border/60 bg-card',
        sunken: 'border-border/60 bg-elevated',
        transparent: 'border-border/60 bg-transparent',
      },
    },
    defaultVariants: {
      size: 'default',
      surface: 'muted',
    },
  }
)

export type EmptyStateProps = Omit<ComponentProps<'div'>, 'title'> &
  VariantProps<typeof emptyStateVariants> & {
    icon?: ComponentType<{ className?: string; 'aria-hidden'?: AriaAttributes['aria-hidden'] }>
    title?: ReactNode
    description?: ReactNode
    action?: ReactNode
  }

function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  size = 'default',
  surface = 'muted',
  className,
  ...props
}: EmptyStateProps) {
  const isCompact = size === 'compact' || size === 'inline'
  const titleVariant = size === 'inline' ? 'supporting' : isCompact ? 'labelStrong' : 'sectionTitle'

  return (
    <div
      data-slot="empty-state"
      data-size={size}
      data-surface={surface}
      className={cn(emptyStateVariants({ size, surface }), className)}
      {...props}
    >
      {Icon ? (
        <span
          className={cn(
            'grid place-items-center rounded-full bg-accentSecondary text-accentSecondary-foreground',
            isCompact ? 'size-9' : 'size-11'
          )}
        >
          <Icon className={isCompact ? 'size-4' : 'size-5'} aria-hidden="true" />
        </span>
      ) : null}
      <div className="max-w-lg space-y-1">
        <Typography variant={titleVariant}>{title}</Typography>
        {description ? <Typography variant="description">{description}</Typography> : null}
      </div>
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  )
}

export { EmptyState, emptyStateVariants }
